/**
 * The realm map's deep-zoom tiles (cut by scripts/tile-map.sh): which level
 * to draw at a zoom, and which of its tiles are on screen. Level `top` is
 * the master painting; each level below halves it, down to one tile.
 */

export interface TileSet {
  /** URL folder holding `{level}/{col}_{row}.webp`. */
  base: string;
  /** The master painting, in pixels. */
  width: number;
  height: number;
  tileSize: number;
  /** The master's level number (levels run 0 to top). */
  top: number;
  /** A cap on the tiles of one level loaded at once, in place of
   *  {@link MAX_TILES} (a wide, short painting can afford more). */
  maxTiles?: number;
}

/** The painted realm map (the producer's pick, 12 October 2026), tiled as
 *  public/realm/v1. A new painting goes in a new version folder. */
export const REALM_TILES: TileSet = {
  base: '/realm/v1/map_files',
  width: 3504,
  height: 2336,
  tileSize: 512,
  top: 3,
};

/** The same painting with the movers painted out where the ambient layer
 *  draws them moving (the clean plate, flag realm_alive): public/realm/v6,
 *  cut from the master with scripts/realm-plate.sh. */
export const REALM_PLATE_TILES: TileSet = { ...REALM_TILES, base: '/realm/v6/map_files' };

/**
 * At most this many tiles of one level load at once (the view plus the
 * margin the map keeps ready round it). Past it, the level below is drawn
 * instead, so the opening view stays within its 1.5 MB budget (the whole
 * map on a desktop draws the level below the master: 12 tiles). The tiling
 * script checks the budget with the same number.
 */
export const MAX_TILES = 20;

/** A rectangle in master pixels. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Tile {
  key: string;
  url: string;
  level: number;
  /** Where the tile sits, in master pixels. */
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Master pixels per pixel of a level. */
export function levelStep(set: TileSet, level: number): number {
  return 2 ** (set.top - level);
}

/** Every tile of `level` that touches `rect` (master pixels). */
export function tilesIn(set: TileSet, level: number, rect: Rect): Tile[] {
  const step = levelStep(set, level);
  const span = set.tileSize * step;
  const cols = Math.ceil(set.width / span);
  const rows = Math.ceil(set.height / span);
  const c0 = Math.max(0, Math.floor(rect.x / span));
  const r0 = Math.max(0, Math.floor(rect.y / span));
  const c1 = Math.min(cols - 1, Math.floor((rect.x + rect.w - 1e-6) / span));
  const r1 = Math.min(rows - 1, Math.floor((rect.y + rect.h - 1e-6) / span));
  const out: Tile[] = [];
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const x = c * span;
      const y = r * span;
      out.push({
        key: `${level}/${c}_${r}`,
        url: `${set.base}/${level}/${c}_${r}.webp`,
        level,
        x,
        y,
        w: Math.min(span, set.width - x),
        h: Math.min(span, set.height - y),
      });
    }
  }
  return out;
}

/**
 * The level to draw at `zoom` (screen pixels per master pixel) on a screen
 * with `dpr` device pixels per CSS pixel: the coarsest level that is still
 * sharp, or a coarser one when the sharp one would need more than
 * `set.maxTiles` (default {@link MAX_TILES}) tiles to cover `view`.
 */
export function pickLevel(set: TileSet, zoom: number, dpr: number, view: Rect): number {
  const need = zoom * Math.max(1, dpr);
  let level = 0;
  while (level < set.top && 1 / levelStep(set, level) < need * 0.95) level++;
  const cap = set.maxTiles ?? MAX_TILES;
  while (level > 0 && tilesIn(set, level, view).length > cap) level--;
  return level;
}
